import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function ServicePage({ eyebrow, title, subtitle, sections, features, slug }) {
  const pageTitle = `${title} | Focus Traffic Plans`;
  const canonicalUrl = `https://focustrafficplans.com.au/services/${slug}`;

  useEffect(() => {
    const previousTitle = document.title;
    const tags = [
      ...document.querySelectorAll(
        'meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]'
      ),
    ];
    const previous = tags.map((tag) => tag.getAttribute("content"));
    document.title = pageTitle;
    tags.forEach((tag) => tag.setAttribute("content", subtitle));
    return () => {
      document.title = previousTitle;
      tags.forEach((tag, index) => tag.setAttribute("content", previous[index] ?? ""));
    };
  }, [pageTitle, subtitle]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={subtitle} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={subtitle} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Focus Traffic Plans" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={subtitle} />
      </Helmet>
      {/* Hero */}
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-orange-500">
            {eyebrow}
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-black sm:text-4xl lg:text-5xl">
            {title}
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-gray-500">
            {subtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-orange-500/20 transition hover:bg-orange-600"
            >
              Get a Quote
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <a
              href="tel:1300118170"
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition hover:border-orange-400 hover:text-orange-500"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              1300 118 170
            </a>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_300px]">

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((s) => (
              <div key={s.heading}>
                <h2 className="mb-3 text-xl font-bold text-black">{s.heading}</h2>
                <p className="text-[15px] leading-relaxed text-gray-500">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Sidebar features */}
          <aside>
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-5 text-xs font-bold uppercase tracking-widest text-gray-400">
                What's included
              </h3>
              <ul className="space-y-3">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm leading-snug text-gray-700">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
              >
                Request this service
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
